// @givmo/fundraisers-module — the Emails tab's reads (EMC-WEB1), under the module's own keys: the family has
// no generated ops (`emails-transport.ts`). A route miss or a refusal is an ANSWER (emails are dark here,
// or not this person's), never a blip: no retry. Anything else keeps the hosts' one retry.

import { isGivmoError } from '@givmo/api-client';
import {
  decodeAudienceOptions,
  decodeEmail,
  decodeEmails,
  decodeEstimate,
  decodePreview,
  decodeResultsSummary,
  type EmailAudience,
  audienceBody,
} from './emails-decode';
import {
  estimateAudience,
  fetchAudienceOptions,
  fetchEmail,
  fetchEmailPreview,
  fetchEmailResultsSummary,
  fetchEmails,
} from './emails-transport';

const retry = (failureCount: number, error: unknown) =>
  !(isGivmoError(error) && (error.status === 404 || error.status === 403)) && failureCount < 1;

export const emailKeys = {
  all: ['fundraisers-module', 'emails'] as const,
  series: (orgRef: string, fundraiserId: string) =>
    [...emailKeys.all, orgRef, fundraiserId, 'series'] as const,
  email: (orgRef: string, fundraiserId: string, emailId: string) =>
    [...emailKeys.all, orgRef, fundraiserId, 'email', emailId] as const,
  preview: (orgRef: string, fundraiserId: string, emailId: string) =>
    [...emailKeys.all, orgRef, fundraiserId, 'email', emailId, 'preview'] as const,
  results: (orgRef: string, fundraiserId: string) =>
    [...emailKeys.all, orgRef, fundraiserId, 'results'] as const,
  options: (orgRef: string, fundraiserId: string) =>
    [...emailKeys.all, orgRef, fundraiserId, 'audience-options'] as const,
  estimate: (orgRef: string, fundraiserId: string, audience: EmailAudience) =>
    [...emailKeys.all, orgRef, fundraiserId, 'estimate', audienceBody(audience)] as const,
};

export function emailsSeriesOptions(orgRef: string, fundraiserId: string) {
  return {
    queryKey: emailKeys.series(orgRef, fundraiserId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchEmails(orgRef, fundraiserId, { signal }).then(decodeEmails),
    retry,
  };
}

/** EMC-BE8: the series' totals ("Gifts from these emails"). */
export function emailResultsSummaryOptions(orgRef: string, fundraiserId: string) {
  return {
    queryKey: emailKeys.results(orgRef, fundraiserId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchEmailResultsSummary(orgRef, fundraiserId, { signal }).then(decodeResultsSummary),
    retry,
  };
}

export function emailOptions(orgRef: string, fundraiserId: string, emailId: string) {
  return {
    queryKey: emailKeys.email(orgRef, fundraiserId, emailId),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const row = decodeEmail(await fetchEmail(orgRef, fundraiserId, emailId, { signal }));
      if (row === null) throw new Error('The email could not be read.');
      return row;
    },
    retry,
  };
}

export function emailPreviewOptions(orgRef: string, fundraiserId: string, emailId: string) {
  return {
    queryKey: emailKeys.preview(orgRef, fundraiserId, emailId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchEmailPreview(orgRef, fundraiserId, emailId, { signal }).then(decodePreview),
    retry,
  };
}

export function audienceOptionsOptions(orgRef: string, fundraiserId: string) {
  return {
    queryKey: emailKeys.options(orgRef, fundraiserId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchAudienceOptions(orgRef, fundraiserId, { signal }).then(decodeAudienceOptions),
    retry,
  };
}

export function audienceEstimateOptions(
  orgRef: string,
  fundraiserId: string,
  audience: EmailAudience,
) {
  return {
    queryKey: emailKeys.estimate(orgRef, fundraiserId, audience),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      estimateAudience(orgRef, fundraiserId, audienceBody(audience), { signal }).then(
        decodeEstimate,
      ),
    retry,
  };
}
